/**
 * Two-column feature showcase with text + visual.
 *
 * Alternates layout direction via `reverse` prop for visual rhythm.
 * In Builder, drop child components into the visual slot.
 * Aliases: feature section, text-image split, product showcase
 */

import { Link } from "@tanstack/react-router";
import { type ReactNode } from "react";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/cn";

type FeatureShowcaseProps = {
  readonly preHeadline?: string;
  readonly headline?: string;
  readonly description?: string;
  readonly visual?: ReactNode;
  readonly ctaText?: string;
  readonly ctaHref?: string;
  readonly reverse?: boolean;
  readonly className?: string;
  readonly children?: ReactNode;
};

export function FeatureShowcase({
  preHeadline,
  headline,
  description,
  visual,
  ctaText,
  ctaHref,
  reverse = false,
  className,
  children,
}: FeatureShowcaseProps): ReactNode {
  return (
    <section className={cn("py-16 sm:py-20", className)}>
      <div
        className={cn(
          "mx-auto grid max-w-screen-xl items-center gap-12 px-4 lg:grid-cols-2 lg:gap-16",
          reverse && "lg:[&>*:first-child]:order-2",
        )}
      >
        <div className="flex flex-col gap-5">
          <div>
            {preHeadline && (
              <p className="mb-1 text-sm font-medium tracking-wide text-purple-700 uppercase">
                {preHeadline}
              </p>
            )}
            {headline && (
              <h2 className="text-text-primary font-heading text-3xl font-medium tracking-tight sm:text-4xl">
                {headline}
              </h2>
            )}
          </div>
          {description && (
            <p className="text-text-secondary text-lg leading-relaxed">
              {description}
            </p>
          )}
          {ctaText && ctaHref && (
            <div className="pt-2">
              <Link to={ctaHref}>
                <Button variant="primary">{ctaText}</Button>
              </Link>
            </div>
          )}
        </div>

        <div className="flex justify-center">{visual ?? children}</div>
      </div>
    </section>
  );
}
